import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import {
  useGetAgentChatHistoryQuery,
  useGetPersonalReceiptsQuery,
  useSendAgentMessageMutation,
  useSendTypingSignalMutation,
  useMarkChatReadMutation,
  useGetPersonalReactionsQuery,
  useReactPersonalMessageMutation,
  useSendVoiceMessageMutation,
  useGetVoiceListenStateQuery,
  useRecordVoiceHeardMutation,
  type ReactionGroup,
} from "../../services/rtkApi/agentApi";
import { useAppSelector } from "../../store/hooks";
import { ChatMessage } from "./ChatMessage";
import { MessageReactions } from "./MessageReactions";
import { AgentStatusBadge } from "./AgentStatusBadge";
import { Button } from "../../ui";
import { MentionTextarea } from "./MentionTextarea";
import { VoiceRecorder } from "./VoiceRecorder";
import { ChatSendButton } from "./ChatSendButton";
import { Mic } from "lucide-react";
import { VirtualizedMessageList, type VirtualizedChatItem } from "./VirtualizedMessageList";
import { useAgentChatScroll } from "./useAgentChatScroll";
import { usePresenceHeartbeat } from "./usePresenceHeartbeat";
import { NewMessagesIndicator } from "./NewMessagesIndicator";
import { ChatMessagesSkeleton } from "./ChatMessagesSkeleton";

const TYPING_THROTTLE_MS = 3_000;

interface PersonalChatProps {
  /** whether this chat view is actually being viewed — gates the heartbeat */
  active?: boolean;
}

export function PersonalChat({ active = true }: PersonalChatProps) {
  const user = useAppSelector((s) => s.auth);
  const isLoggedIn = user.bootstrapStatus === "ready" && !!user.token;

  const [sendTyping] = useSendTypingSignalMutation();
  const lastTypingRef = useRef(0);

  // only register presence while this chat view is actually open + visible
  usePresenceHeartbeat("personal", active);

  const { data, isLoading, refetch } = useGetAgentChatHistoryQuery(
    { limit: 100 },
    { skip: !isLoggedIn, pollingInterval: 20_000 },
  );

  // bot read/delivered cursors — drives the "delivered / seen by sitebot" indicator on the
  // user's own messages
  const { data: receipts } = useGetPersonalReceiptsQuery(undefined, {
    skip: !isLoggedIn || !active,
    pollingInterval: 20_000,
  });

  // while this chat is actually on screen, keep the server read cursor advanced
  // so a message seen here never re-notifies (here or on another browser/device)
  const [markChatRead] = useMarkChatReadMutation();
  const lastMarkedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active || !isLoggedIn) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const msgs = data?.messages;
    if (!msgs || msgs.length === 0) return;
    const latest = msgs[msgs.length - 1].timestamp;
    if (lastMarkedRef.current && latest <= lastMarkedRef.current) return;
    lastMarkedRef.current = latest;
    markChatRead({ personal: latest });
  }, [active, isLoggedIn, data, markChatRead]);

  // reactions for this personal thread — polled + live SSE deltas inside the query
  const { data: reactionsData } = useGetPersonalReactionsQuery(undefined, {
    skip: !isLoggedIn || !active,
    pollingInterval: 20_000,
  });
  const [reactToMessage] = useReactPersonalMessageMutation();

  const [sendMessage, { isLoading: isSending }] = useSendAgentMessageMutation();
  const [sendVoice, { isLoading: isSendingVoice }] = useSendVoiceMessageMutation();
  const [recordHeard] = useRecordVoiceHeardMutation();
  const [voiceMode, setVoiceMode] = useState(false);
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const messages = useMemo(() => data?.messages ?? [], [data]);

  // listen-state for the voice notes currently in view (hear-once + one replay)
  const voiceMids = useMemo(
    () => messages.filter((m) => m.voice && m.mid).map((m) => m.mid as string),
    [messages],
  );
  const { data: voiceState } = useGetVoiceListenStateQuery(voiceMids, {
    skip: voiceMids.length === 0,
  });
  const voiceMaxPlays = voiceState?.maxPlays ?? 2;

  const reactionsByMid = useMemo(() => {
    const map = new Map<string, ReactionGroup[]>();
    for (const r of reactionsData?.reactions ?? []) {
      const arr = map.get(r.mid) ?? [];
      arr.push(r);
      map.set(r.mid, arr);
    }
    return map;
  }, [reactionsData]);

  const { scrollRef, onScroll, scrollToBottom, pendingNewCount } = useAgentChatScroll(
    messages.length,
  );

  const vItems: VirtualizedChatItem[] = useMemo(
    () =>
      messages.map((msg, i) => ({
        id: msg.mid ?? `personal-${msg.timestamp}-${i}`,
        mid: msg.mid,
        content: msg.content,
        sender: msg.role === "assistant" ? "sitebot" : user.username || "you",
        senderType: msg.role === "assistant" ? "agent" : "self",
        timestamp: msg.timestamp,
        voice: msg.voice,
      })),
    [messages, user.username],
  );

  const handleTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingRef.current > TYPING_THROTTLE_MS) {
      lastTypingRef.current = now;
      sendTyping({ chatType: "personal" });
    }
  }, [sendTyping]);

  const handleSend = async () => {
    if (!text.trim() || isSending) return;
    const msg = text.trim();
    setText("");
    try {
      await sendMessage({ text: msg }).unwrap();
      refetch();
    } catch {
      // keep text cleared; user can retry
    }
    inputRef.current?.focus();
  };

  const handleSendVoice = async (audio: Blob, durationMs: number) => {
    try {
      await sendVoice({ audio, target: "personal", durationMs }).unwrap();
      setVoiceMode(false);
      refetch();
    } catch (err) {
      const msg =
        (err as { data?: { error?: string } })?.data?.error ?? "couldn't send the voice message";
      toast.error(msg, { position: "bottom-right" });
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm max-[700px]:text-xs bg-paper">
        log in to chat with the agent
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-paper">
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b-2 border-ink bg-white/50 max-[700px]:px-3 max-[700px]:py-1.5">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="font-bold text-sm shrink-0 max-[700px]:text-xs">sitebot</span>
          <AgentStatusBadge />
        </div>
        <span className="text-xs text-gray-400 shrink-0 max-[700px]:text-[10px]">personal</span>
      </div>

      <div className="relative flex-1 flex flex-col min-h-0">
        {isLoading ? (
          <ChatMessagesSkeleton />
        ) : messages.length === 0 ? (
          <div className="flex-1 overflow-y-auto p-4 text-center text-gray-400 text-sm max-[700px]:text-xs py-8">
            no messages yet — say hi!
          </div>
        ) : (
          <>
            <VirtualizedMessageList
              scrollRef={scrollRef}
              onScroll={onScroll}
              items={vItems}
              renderItem={(item) => {
                let status: "delivered" | "read" | undefined;
                if (item.senderType === "self") {
                  const t = new Date(item.timestamp).getTime();
                  const botRead = receipts?.bot.personalLastReadAt;
                  const botDelivered = receipts?.bot.personalDeliveredAt;
                  if (botRead && new Date(botRead).getTime() >= t) status = "read";
                  else if (botDelivered && new Date(botDelivered).getTime() >= t)
                    status = "delivered";
                }
                return (
                  <ChatMessage
                    sender={item.sender}
                    senderType={item.senderType}
                    content={item.content}
                    timestamp={item.timestamp}
                    status={status}
                    statusLabel="seen by sitebot"
                    voice={item.voice}
                    voicePlays={item.mid ? voiceState?.states[item.mid]?.plays ?? 0 : 0}
                    voiceMaxPlays={voiceMaxPlays}
                    onPlayVoice={item.mid ? () => recordHeard({ voiceId: item.mid as string }) : undefined}
                  />
                );
              }}
              renderReactions={(item) =>
                item.mid ? (
                  <MessageReactions
                    reactions={reactionsByMid.get(item.mid) ?? []}
                    onToggle={(emoji) => reactToMessage({ mid: item.mid as string, emoji })}
                    align={item.senderType === "self" ? "end" : "start"}
                  />
                ) : null
              }
            />
            <NewMessagesIndicator
              count={pendingNewCount}
              onJump={() => scrollToBottom("smooth")}
            />
          </>
        )}
      </div>

      <div className="border-t-2 border-ink p-3 bg-white/50 max-[700px]:p-2 shrink-0">
        {voiceMode ? (
          <div className="flex items-center gap-2">
            <VoiceRecorder
              onSend={handleSendVoice}
              onCancel={() => setVoiceMode(false)}
              sending={isSendingVoice}
              iosTapOverlay
            />
          </div>
        ) : (
          <form
            className="flex gap-2 items-end"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
          >
            <Button
              type="button"
              size="sm"
              variant="ghost"
              iosTapOverlay
              onClick={() => setVoiceMode(true)}
              className="shrink-0 max-[700px]:w-9 max-[700px]:px-0"
              aria-label="record a voice message"
              title="record a voice message"
            >
              <Mic className="w-4 h-4" />
            </Button>
            <MentionTextarea
              ref={inputRef}
              value={text}
              onChange={(v) => {
                setText(v);
                if (v.trim()) handleTyping();
              }}
              onSubmit={handleSend}
              placeholder="type a message... (@ to mention)"
              maxLength={2000}
              disabled={isSending}
              mentionCandidates={["sitebot"]}
            />
            <ChatSendButton disabled={!text.trim() || isSending} loading={isSending} />
          </form>
        )}
      </div>
    </div>
  );
}
